import classNames from "classnames";
import React from "react";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./action-section.scss";

export interface IActionSectionTitleProps {
  /** "Actions", "Screen Inputs", or a host's own `customHeader`. */
  title: React.ReactNode;
  /** Number of items in the section. The count badge is hidden entirely at 0 — a "0" badge beside
   *  "No actions yet." would say the same thing twice. */
  count: number;
  className?: string;
}

/**
 * The section title + its count badge — no row/layout of its own. It is meant to drop into
 * whichever flex/legacy header row a host already lays out (`ActionPicker`'s
 * `kuika_actionPanelWrapperHeader`, `ScreenInputList`'s own header div), replacing only the title
 * text node, so none of those hosts' existing layout markup has to change.
 */
export const ActionSectionTitle: React.FC<IActionSectionTitleProps> = ({ title, count, className }) => (
  <span className={classNames("kuika-action-section__title-group", className)}>
    <span className="kuika-action-section__title">{title}</span>
    {count > 0 && <KuikaBadge content={String(count)} type="label" color="grey" variant="subtle" size="small" />}
  </span>
);

export interface IActionSectionAddButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "color" | "onClick" | "children"
> {
  ariaLabel: string;
  testId: string;
  /** Optional when the button is a popover trigger — the popover owns the click then. */
  onClick?: () => void;
  className?: string;
  /** For a host whose add is off for a while — the Style field while a tag is being edited. */
  disabled?: boolean;
  /** The glyph (public/images/common). "plus" by default; the Actions header's paste uses "paste" so the
   *  two header controls are one family. */
  iconName?: string;
}

/** The 24px tinted "+" icon button that replaced the old "+ ADD ACTION" / "+ ADD SCREEN INPUT" text
 *  links, and the one the Styling tab's Style field adds tags with. Purely presentational — callers
 *  own what the click does (open a picker, start a new row, …). Forwards its ref and any other props
 *  to the button, so it can be a Radix `Trigger asChild` (the action picker anchors its popup to it). */
export const ActionSectionAddButton = React.forwardRef<HTMLButtonElement, IActionSectionAddButtonProps>(
  ({ ariaLabel, testId, onClick, className, disabled = false, iconName = "plus", ...rest }, ref) => (
    <KuikaButton
      {...rest}
      ref={ref}
      type="tertiary"
      isIcon
      size="sm"
      tinted
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      className={classNames("kuika-action-section__add-button", className)}
      onClick={onClick}
    >
      <KuikaSvg name={iconName} type="common" width={14} height={14} />
    </KuikaButton>
  )
);
ActionSectionAddButton.displayName = "ActionSectionAddButton";
